import { ChevronDownIcon } from "@heroicons/react/outline";
import { AnimatePresence, motion } from "framer-motion";
import { observer } from "mobx-react-lite";

import blocks, { BlockType } from "../Blocks";

import BlockSelector from "./BlockSelector";
import { useBlockEnv } from "./env-context";
import { useIsReadonly } from "./readonly-context";

import { BlockProps } from ".";

import { BasicTooltip } from "@/components/ui/Tooltip";

interface BlockHeaderTriggerProps {
  type: BlockType;
  isEdited: boolean;
  isReadonly: boolean;
}
const BlockHeaderTrigger = ({ type, isEdited, isReadonly }: BlockHeaderTriggerProps) => {
  const blockDefinition = blocks[type];

  const IconComponent = blockDefinition.icon;

  const Component = (
    <div
      className={`group flex flex-row ${
        isReadonly
          ? "cursor-not-allowed"
          : "cursor-pointer border px-3  hover:border-gray-300 hover:bg-gray-50 dark:hover:border-gray-600"
      } select-none items-center rounded-lg  py-2 transition-all duration-200 dark:hover:bg-gray-800`}
    >
      <div
        className={`flex h-10 w-10 items-center justify-center rounded-lg transition-all duration-200 ${
          isEdited ? "bg-amber-50 group-hover:bg-amber-100" : "bg-blue-50 group-hover:bg-blue-100"
        }`}
      >
        <IconComponent
          className={`h-5 w-5 transition-all duration-200 ${
            isEdited
              ? "text-amber-600 group-hover:text-amber-700"
              : "text-blue-600 group-hover:text-blue-700"
          }`}
        />
      </div>
      <div className="ml-3 flex flex-1 flex-row items-center justify-center">
        <div className="flex flex-1 flex-col justify-center">
          <div className="flex flex-row items-center">
            <div className="flex flex-1 items-center gap-2">
              <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">{type}</h2>
              {isReadonly && (
                <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                  Read Only
                </span>
              )}
            </div>
          </div>
          {!isReadonly && (
            <span className="text-xs text-gray-500 dark:text-gray-400">Click to change type</span>
          )}
        </div>
        {!isReadonly && (
          <div className="flex items-center justify-center">
            <ChevronDownIcon className="h-4 w-4 text-gray-400 transition-transform duration-200 group-hover:rotate-180" />
          </div>
        )}
      </div>
    </div>
  );

  if (isReadonly) {
    return Component;
  }

  return (
    <BasicTooltip content="Click to change block type" side="left">
      {Component}
    </BasicTooltip>
  );
};

interface BlockHeaderProps {
  type?: BlockType;
  isDraft?: boolean;
  onTypeChange?: BlockProps["onTypeChange"];
  isScrolled?: boolean;
  headerInjection?: React.ReactNode | ((isScrolled: boolean) => React.ReactNode);
}

const BlockHeader = ({
  type,
  isDraft,
  onTypeChange,
  isScrolled = false,
  headerInjection,
}: BlockHeaderProps) => {
  const isReadonly = useIsReadonly();

  const env = useBlockEnv();
  if (!type) {
    return null;
  }

  const blockDefinition = blocks[type];

  if (!blockDefinition) {
    return null;
  }

  const isEdited = Boolean(isDraft) && !isReadonly;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="w-full">
          <BlockSelector
            customTrigger={
              <BlockHeaderTrigger type={type} isEdited={isEdited} isReadonly={isReadonly} />
            }
            block={type}
            env={env}
            disabled={isReadonly}
            onSelect={block => onTypeChange?.(block)}
          />
        </div>

        {headerInjection && (
          <div className="flex items-center">
            {typeof headerInjection === "function" ? headerInjection(isScrolled) : headerInjection}
          </div>
        )}
      </div>

      <AnimatePresence mode="wait">
        {!isScrolled && (
          <motion.div
            initial={{ opacity: 0, height: 0, marginTop: 0 }}
            animate={{
              opacity: 1,
              height: "auto",
              marginTop: 12,
              transition: {
                height: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
                opacity: { duration: 0.25, delay: 0.1, ease: "easeOut" },
                marginTop: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
              },
            }}
            exit={{
              opacity: 0,
              height: 0,
              marginTop: 0,
              transition: {
                height: { duration: 0.25, ease: [0.4, 0, 0.2, 1] },
                opacity: { duration: 0.15, ease: "easeIn" },
                marginTop: { duration: 0.25, ease: [0.4, 0, 0.2, 1] },
              },
            }}
            className="overflow-hidden"
          >
            <div className="w-[350px] text-sm text-gray-600 dark:text-gray-400">
              {typeof blockDefinition.long_description === "string" ? (
                <p>{blockDefinition.long_description}</p>
              ) : (
                blockDefinition.long_description
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default observer(BlockHeader);
